/**
 * Dispatch desk. MASTER_PROMPT sections 6.14 and 8.7.
 *
 * Three steps in the order the yard does them: raise the consignment, weigh
 * it, close it. The weighbridge three-way check sits between weighing and
 * closing, because that is the moment when a difference can still be
 * investigated rather than explained away a month later.
 *
 * Phase 0 finding F6 showed seven June dispatches where the bridge read
 * heavier than declared — every one in the same direction. The exceptions
 * panel exists to make that pattern visible rather than leaving it as seven
 * unrelated rows.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError, OfflineQueued } from "../../lib/api";
import { ops } from "../../lib/api2";
import type {
  DispatchResult,
  DispatchRow,
  Party,
  Sku,
  WeighbridgeReport,
} from "../../lib/types";
import { fmtDate, fmtQty, fmtStatus, today } from "../../lib/format";
import { PeriodPicker } from "../../components/PeriodPicker";
import { SheetHead, SheetToolbar } from "../../components/Sheet";
import { useSheet, type SheetColumn } from "../../lib/sheet";
import { useReportPeriod } from "../../lib/reportPeriod";
import { ReportDownload } from "../../components/ReportDownload";

type Tab = "raise" | "register" | "weighbridge";

interface Line {
  key: string;
  skuCode: string;
  qty: string;
  rate: string;
}

const blank = (): Line => ({
  key: Math.random().toString(36).slice(2),
  skuCode: "",
  qty: "",
  rate: "",
});


const REGISTER_COLUMNS: SheetColumn<DispatchRow>[] = [
  { key: "doc_no", label: "Document", value: (r) => r.doc_no },
  { key: "date", label: "Date", type: "date", value: (r) => r.business_date,
    format: (r) => fmtDate(r.business_date) },
  { key: "vehicle", label: "Vehicle", value: (r) => r.vehicle_no ?? "" },
  { key: "bags", label: "Bags", type: "number", numeric: true,
    value: (r) => Number(r.total_pcs ?? 0), format: (r) => fmtQty(r.total_pcs) },
  { key: "required", label: "Required", type: "number", numeric: true,
    value: (r) => Number(r.required_wt_kg ?? 0),
    format: (r) => fmtQty(r.required_wt_kg, 1) },
  { key: "declared", label: "Declared", type: "number", numeric: true,
    value: (r) => Number(r.declared_wt_kg ?? 0),
    format: (r) => fmtQty(r.declared_wt_kg, 1) },
  { key: "bridge", label: "Bridge", type: "number", numeric: true,
    value: (r) => Number(r.bridge_wt_kg ?? 0),
    format: (r) => fmtQty(r.bridge_wt_kg, 1) },
  { key: "difference", label: "Difference", type: "number", numeric: true,
    value: (r) => Number(r.variance_vs_bridge ?? 0),
    format: (r) => r.variance_vs_bridge === null
      ? "—" : fmtQty(r.variance_vs_bridge, 1) },
  { key: "status", label: "Status", value: (r) => fmtStatus(r.status) },
];

type ExceptionRow = WeighbridgeReport["exceptions"][number];

const EXCEPTION_COLUMNS: SheetColumn<ExceptionRow>[] = [
  { key: "doc_no", label: "Document", value: (r) => r.doc_no },
  { key: "date", label: "Date", type: "date", value: (r) => r.business_date,
    format: (r) => fmtDate(r.business_date) },
  { key: "declared", label: "Declared", type: "number", numeric: true,
    value: (r) => Number(r.declared_wt_kg ?? 0),
    format: (r) => fmtQty(r.declared_wt_kg, 1) },
  { key: "bridge", label: "Bridge", type: "number", numeric: true,
    value: (r) => Number(r.bridge_wt_kg ?? 0),
    format: (r) => fmtQty(r.bridge_wt_kg, 1) },
  { key: "difference", label: "Difference", type: "number", numeric: true,
    value: (r) => Number(r.gap_kg ?? 0), format: (r) => fmtQty(r.gap_kg, 1) },
  // A report that is one-sided means the bridge is out of calibration rather
  // than the loading being sloppy, so filtering by direction is the check.
  { key: "direction", label: "Direction", value: (r) => r.direction },
];

export function DispatchDesk() {
  const [tab, setTab] = useState<Tab>("raise");
  const [skus, setSkus] = useState<Sku[]>([]);
  const [banner, setBanner] = useState<{
    tone: "positive" | "negative" | "warning";
    text: string;
  } | null>(null);

  // -- Raise -------------------------------------------------------------
  // Customers come from masters. The hardcoded six meant a new customer
  // could not be dispatched to until somebody edited this file, and one that
  // stopped trading stayed on the list indefinitely.
  const [customers, setCustomers] = useState<Party[]>([]);
  const [party, setParty] = useState("");
  const [businessDate, setBusinessDate] = useState(today);
  const [vehicle, setVehicle] = useState("");
  const [destination, setDestination] = useState("");
  const [lines, setLines] = useState<Line[]>([blank(), blank()]);
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState<DispatchResult | null>(null);

  // -- Weigh -------------------------------------------------------------
  const [declared, setDeclared] = useState("");
  const [bridge, setBridge] = useState("");
  const [override, setOverride] = useState("");

  // -- Register and exceptions -------------------------------------------
  // One period for the register and the weighbridge report both, chosen from
  // the shared presets rather than two date boxes per tab.
  // Dispatch has no shift: a lorry leaves the gate, not a shift.
  const [shared, setPeriod] = useReportPeriod();
  const period = useMemo(() => ({ ...shared, shift: undefined }), [shared]);
  const { from, to } = period;
  const [rows, setRows] = useState<DispatchRow[] | null>(null);
  const [report, setReport] = useState<WeighbridgeReport | null>(null);

  useEffect(() => {
    ops.skus().then(setSkus).catch(() => undefined);
    api
      .parties("customer")
      .then((rows) => {
        setCustomers(rows);
        setParty((current) => current || rows[0]?.code || "");
      })
      .catch(() => undefined);
  }, []);

  const loadRegister = useCallback(() => {
    setRows(null);
    ops
      .dispatches(from, to)
      .then(setRows)
      .catch((e: ApiError) => setBanner({ tone: "negative", text: e.message }));
  }, [from, to]);

  const loadReport = useCallback(() => {
    setReport(null);
    ops
      .weighbridge(from, to)
      .then(setReport)
      .catch((e: ApiError) => setBanner({ tone: "negative", text: e.message }));
  }, [from, to]);

  useEffect(() => {
    if (tab === "register") loadRegister();
    if (tab === "weighbridge") loadReport();
  }, [tab, loadRegister, loadReport]);

  const register = useSheet(rows ?? [], REGISTER_COLUMNS);
  const exceptions = useSheet(report?.exceptions ?? [], EXCEPTION_COLUMNS);

  // Required weight is derived as you type, so the yard sees the target
  // before the truck reaches the bridge.
  const requiredKg = useMemo(
    () =>
      lines.reduce((sum, l) => {
        const sku = skus.find((s) => s.code === l.skuCode);
        const qty = Number(l.qty);
        if (!sku?.wt_per_bag_g || !Number.isFinite(qty)) return sum;
        return sum + (qty * Number(sku.wt_per_bag_g)) / 1000;
      }, 0),
    [lines, skus]
  );

  function update(index: number, patch: Partial<Line>) {
    setLines((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      if (index === next.length - 1) next.push(blank());
      return next;
    });
  }

  async function raise() {
    const payload = lines
      .filter((l) => l.skuCode && Number(l.qty) > 0)
      .map((l) => ({
        sku_code: l.skuCode,
        qty_pcs: Number(l.qty),
        rate: l.rate ? Number(l.rate) : null,
      }));

    if (!payload.length) {
      setBanner({ tone: "warning", text: "Add at least one line." });
      return;
    }

    setBusy(true);
    setBanner(null);
    try {
      const result = await ops.createDispatch({
        party_code: party,
        business_date: businessDate,
        vehicle_no: vehicle || null,
        destination: destination || null,
        lines: payload,
      });
      setCurrent(result);
      setDeclared("");
      setBridge("");
      setBanner({
        tone: "positive",
        text: `${result.doc_no} raised — ${fmtQty(result.total_pcs)} bags, ${fmtQty(result.required_kg, 1)} kg required.`,
      });
      setLines([blank(), blank()]);
    } catch (err) {
      if (err instanceof OfflineQueued) {
        setBanner({
          tone: "warning",
          text: "No connection. Saved on this device and it will sync.",
        });
        return;
      }
      const e = err as ApiError;
      setBanner({ tone: "negative", text: e.blocked?.message ?? e.message });
    } finally {
      setBusy(false);
    }
  }

  async function weigh() {
    if (!current || !Number(declared)) {
      setBanner({ tone: "warning", text: "Enter the declared weight." });
      return;
    }
    setBusy(true);
    try {
      const result = await ops.weighDispatch(current.dispatch_id, {
        declared_wt_kg: Number(declared),
        bridge_wt_kg: bridge ? Number(bridge) : null,
      });
      setCurrent(result);
      setBanner(
        result.within_tolerance
          ? { tone: "positive", text: "Weights are within tolerance." }
          : { tone: "warning", text: result.warnings.join(" ") }
      );
    } catch (err) {
      setBanner({ tone: "negative", text: (err as ApiError).message });
    } finally {
      setBusy(false);
    }
  }

  async function closeIt() {
    if (!current) return;
    setBusy(true);
    try {
      const result = await ops.closeDispatch(
        current.dispatch_id,
        override || undefined
      );
      setCurrent(result);
      setBanner({ tone: "positive", text: `${result.doc_no} closed.` });
      setOverride("");
    } catch (err) {
      const e = err as ApiError;
      // 422 means it needs an explanation, not that it is broken.
      setBanner({
        tone: e.status === 422 ? "warning" : "negative",
        text: e.message,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h2 className="page__title">Dispatch</h2>
          <p className="page__sub">
            Raise, weigh, close — with the three-way weight check in between
          </p>
        </div>
        <div className="page__controls">
          <div className="segmented" role="tablist">
            {(
              [
                ["raise", "Raise"],
                ["register", "Register"],
                ["weighbridge", "Weighbridge"],
              ] as [Tab, string][]
            ).map(([id, label]) => (
              <button
                key={id}
                className={tab === id ? "is-active" : ""}
                onClick={() => setTab(id)}
                role="tab"
                aria-selected={tab === id}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {banner && (
        <div className={`banner banner--${banner.tone}`} role="alert">
          {banner.text}
        </div>
      )}

      {tab === "raise" && (
        <>
          <div className="page__controls" style={{ marginBottom: "var(--sp-4)" }}>
            <label className="inputgroup">
              <span className="label">Customer</span>
              <select value={party} onChange={(e) => setParty(e.target.value)}>
                {customers.map(({ code, name }) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </select>
            </label>
            <label className="inputgroup">
              <span className="label">Date</span>
              <input
                type="date"
                value={businessDate}
                onChange={(e) => setBusinessDate(e.target.value)}
              />
            </label>
            <label className="inputgroup">
              <span className="label">Vehicle</span>
              <input
                value={vehicle}
                onChange={(e) => setVehicle(e.target.value.toUpperCase())}
                placeholder="BR01AB1234"
              />
            </label>
            <label className="inputgroup">
              <span className="label">Destination</span>
              <input
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
              />
            </label>
          </div>

          <div className="tablewrap">
            <table className="table table--entry">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th className="num">Bags</th>
                  <th className="num">Rate</th>
                  <th className="num">Required weight</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => {
                  const sku = skus.find((s) => s.code === line.skuCode);
                  const kg =
                    sku?.wt_per_bag_g && Number(line.qty)
                      ? (Number(line.qty) * Number(sku.wt_per_bag_g)) / 1000
                      : null;
                  return (
                    <tr key={line.key}>
                      <td>
                        <select
                          value={line.skuCode}
                          onChange={(e) =>
                            update(index, { skuCode: e.target.value })
                          }
                        >
                          <option value="">Choose an SKU…</option>
                          {skus.map((s) => (
                            <option key={s.code} value={s.code}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          className="num mono"
                          inputMode="numeric"
                          value={line.qty}
                          placeholder="0"
                          onChange={(e) => update(index, { qty: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className="num mono"
                          inputMode="decimal"
                          value={line.rate}
                          placeholder="—"
                          onChange={(e) =>
                            update(index, { rate: e.target.value })
                          }
                        />
                      </td>
                      <td className="num mono">
                        {kg === null ? "—" : `${fmtQty(kg, 1)} kg`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3} className="num">
                    Required weight
                  </td>
                  <td className="num mono">{fmtQty(requiredKg, 1)} kg</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="page__footer">
            <span className="entrytotals__sub">
              The weight the consignment should be, from the specified bag
              weight for each SKU.
            </span>
            <button className="btn btn--auto" onClick={raise} disabled={busy}>
              {busy ? <span className="spinner" /> : null}
              Raise dispatch
            </button>
          </div>

          {current && (
            <section className="card weighcard">
              <h3 className="section-title">{current.doc_no}</h3>
              <p className="section-note">
                {fmtQty(current.total_pcs)} bags · three-way weight check
              </p>

              <div className="weighgrid">
                <div className="weighgrid__cell">
                  <span className="label">Required</span>
                  <span className="num mono weighgrid__value">
                    {fmtQty(current.required_kg, 1)}
                  </span>
                  <span className="kpi__sub">from bag specification</span>
                </div>
                <div className="weighgrid__cell">
                  <span className="label">Declared</span>
                  <input
                    className="num mono"
                    inputMode="decimal"
                    value={declared}
                    placeholder="0.0"
                    onChange={(e) => setDeclared(e.target.value)}
                  />
                  <span className="kpi__sub">what the plant loaded</span>
                </div>
                <div className="weighgrid__cell">
                  <span className="label">Weighbridge</span>
                  <input
                    className="num mono"
                    inputMode="decimal"
                    value={bridge}
                    placeholder="0.0"
                    onChange={(e) => setBridge(e.target.value)}
                  />
                  <span className="kpi__sub">what the bridge read</span>
                </div>
                <div
                  className={`weighgrid__cell weighgrid__cell--${
                    current.declared_kg === null
                      ? "idle"
                      : current.within_tolerance
                        ? "ok"
                        : "off"
                  }`}
                >
                  <span className="label">Difference</span>
                  <span className="num mono weighgrid__value">
                    {current.bridge_vs_declared === null
                      ? "—"
                      : `${fmtQty(current.bridge_vs_declared, 1)} kg`}
                  </span>
                  <span className="kpi__sub">bridge against declared</span>
                </div>
              </div>

              {current.warnings.length > 0 && (
                <div className="banner banner--warning">
                  <div>
                    {current.warnings.map((w) => (
                      <div key={w}>{w}</div>
                    ))}
                    <label className="inputgroup" style={{ marginTop: 8 }}>
                      <span className="label">
                        Reason for closing outside tolerance
                      </span>
                      <input
                        value={override}
                        onChange={(e) => setOverride(e.target.value)}
                        placeholder="Bridge recalibrated next morning"
                      />
                    </label>
                  </div>
                </div>
              )}

              <div className="page__footer">
                <button
                  className="btn btn--ghost btn--auto"
                  onClick={weigh}
                  disabled={busy}
                >
                  Record weights
                </button>
                <button
                  className="btn btn--auto"
                  onClick={closeIt}
                  disabled={busy || current.declared_kg === null}
                >
                  Close dispatch
                </button>
              </div>
            </section>
          )}
        </>
      )}

      {tab === "register" && (
        <>
          <div className="page__controls" style={{ marginBottom: "var(--sp-4)" }}>
            <PeriodPicker value={period} onChange={setPeriod} />
            <ReportDownload title="Dispatch" period={period} />
          </div>

          {!rows ? (
            <div className="state">
              {[0, 1, 2].map((i) => (
                <div key={i} className="skeleton state__bar" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="state state--empty">
              <span className="state__glyph" aria-hidden="true">
                ↗
              </span>
              <h3>No dispatches in this period</h3>
              <p>Raise one from the Raise tab.</p>
            </div>
          ) : (
            <>
            <SheetToolbar sheet={register} exportName="dispatch-register" />
            <div className="tablewrap">
              <table className="table">
                <SheetHead sheet={register} />
                <tbody>
                  {register.rows.map((r) => {
                    const gap = Number(r.variance_vs_bridge ?? 0);
                    const flagged = Math.abs(gap) > 50;
                    return (
                      <tr key={r.dispatch_id}>
                        <td className="mono">{r.doc_no}</td>
                        <td>{fmtDate(r.business_date)}</td>
                        <td className="mono">{r.vehicle_no ?? "—"}</td>
                        <td className="num mono">{fmtQty(r.total_pcs)}</td>
                        <td className="num mono">
                          {fmtQty(r.required_wt_kg, 1)}
                        </td>
                        <td className="num mono">
                          {fmtQty(r.declared_wt_kg, 1)}
                        </td>
                        <td className="num mono">{fmtQty(r.bridge_wt_kg, 1)}</td>
                        <td
                          className={`num mono${flagged ? " cell--negative" : ""}`}
                        >
                          {r.variance_vs_bridge === null
                            ? "—"
                            : `${flagged ? "▲ " : ""}${fmtQty(gap, 1)}`}
                        </td>
                        <td>
                          <span className="pill">{fmtStatus(r.status)}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </>
      )}

      {tab === "weighbridge" && (
        <>
          <div className="page__controls" style={{ marginBottom: "var(--sp-4)" }}>
            <PeriodPicker value={period} onChange={setPeriod} />
            <ReportDownload title="Dispatch" period={period} />
          </div>

          {!report ? (
            <div className="state">
              <div className="skeleton state__bar" />
            </div>
          ) : (
            <>
              {/* The diagnosis, not just the list. A one-sided run of
                  differences is calibration; a spread is noise. */}
              <div
                className={`banner banner--${
                  report.one_sided ? "warning" : "positive"
                }`}
              >
                {report.note}
              </div>

              <div className="kpirow">
                <div className="kpi">
                  <span className="label">Checked</span>
                  <span className="kpi-value num">{report.checked}</span>
                  <span className="kpi__sub">dispatches with a bridge reading</span>
                </div>
                <div className="kpi">
                  <span className="label">Bridge heavier</span>
                  <span className="kpi-value num">{report.bridge_heavier}</span>
                  <span className="kpi__sub">beyond 50 kg</span>
                </div>
                <div className="kpi">
                  <span className="label">Bridge lighter</span>
                  <span className="kpi-value num">{report.bridge_lighter}</span>
                  <span className="kpi__sub">beyond 50 kg</span>
                </div>
                <div className="kpi">
                  <span className="label">Net difference</span>
                  <span className="kpi-value num">
                    {fmtQty(report.net_gap_kg, 1)}
                  </span>
                  <span className="kpi__sub">kg across the period</span>
                </div>
              </div>

              {report.exceptions.length === 0 ? (
                <div className="state state--empty">
                  <span className="state__glyph" aria-hidden="true">
                    ✓
                  </span>
                  <h3>Nothing outside tolerance</h3>
                  <p>Every dispatch weighed within 50 kg of the bridge.</p>
                </div>
              ) : (
                <>
                <SheetToolbar sheet={exceptions}
                              exportName="weighbridge-exceptions" />
                <div className="tablewrap">
                  <table className="table">
                    <SheetHead sheet={exceptions} />
                    <tbody>
                      {exceptions.rows.map((e) => (
                        <tr key={e.doc_no}>
                          <td className="mono">{e.doc_no}</td>
                          <td>{fmtDate(e.business_date)}</td>
                          <td className="num mono">
                            {fmtQty(e.declared_wt_kg, 1)}
                          </td>
                          <td className="num mono">
                            {fmtQty(e.bridge_wt_kg, 1)}
                          </td>
                          <td className="num mono cell--negative">
                            {fmtQty(e.gap_kg, 1)}
                          </td>
                          <td>
                            {e.direction === "BRIDGE_HEAVIER"
                              ? "↑ bridge heavier"
                              : "↓ bridge lighter"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
